import { useState } from 'react';
import { useNavigate } from 'react-router';
import { MapPin, Wrench, AlertCircle, CalendarClock } from 'lucide-react';
import {
  STATE_COLORS,
  STATE_LABELS,
  type MonthlyTicketItem,
} from '@sime/shared';
import { RescheduleDialog } from './reschedule-dialog';
import { DocumentosBadge } from '@/components/ui/documentos-badge';
import { NeuButton } from '@/components/ui/neu-button';
import { cn } from '@/lib/utils';

const EXECUTABLE_STATES = new Set<string>(['PROGRAMADO', 'INICIADO']);
const SHORT_MONTHS = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
];

export interface MobileTicketRowProps {
  ticket: MonthlyTicketItem;
  /** Click handler for the row body — typically navigates to ticket detail. */
  onOpen: (id: number) => void;
  /**
   * When true, renders a "DD / MMM" pill on the left edge so the row works in
   * cross-day list contexts (monthly mobile list). Daily/weekly views already
   * group tickets under day headers and pass false (default).
   */
  showDate?: boolean;
}

/**
 * Mobile-friendly clickable ticket row used by daily, weekly, and monthly
 * views. Tapping the row opens the ticket detail; the inline "Ejecutar" CTA
 * jumps directly to the execution screen for PROGRAMADO/INICIADO tickets.
 *
 * Replaces three earlier divergent inline lists in the calendar components
 * that lacked navigation handlers — field workers couldn't reach the
 * execution page from weekly/monthly mobile views (only HOY and Lista
 * worked).
 */
export function MobileTicketRow({ ticket, onOpen, showDate = false }: MobileTicketRowProps) {
  const colors = STATE_COLORS[ticket.estadoOt];
  const isExecutable = EXECUTABLE_STATES.has(ticket.estadoOt);
  const navigate = useNavigate();
  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  const handleExecute = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Carry the current calendar URL (path + filters) so the execution "Volver"
    // returns to where the user was — same breadcrumb as the row-tap onOpen.
    void navigate(`/tickets/${ticket.id}/ejecutar`, {
      state: { from: window.location.pathname + window.location.search },
    });
  };

  const handleReschedule = (e: React.MouseEvent) => {
    e.stopPropagation();
    setRescheduleOpen(true);
  };

  const dateDay = ticket.fecha.slice(8, 10);
  const dateMonth = SHORT_MONTHS[Number(ticket.fecha.slice(5, 7)) - 1] ?? '';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => { onOpen(ticket.id); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(ticket.id);
        }
      }}
      data-testid={`mobile-ticket-row-${ticket.id}`}
      className={cn(
        'group flex items-start gap-3 rounded-xl border-l-4 bg-sime-surface px-3 py-3 shadow-neu-xs',
        'cursor-pointer hover:shadow-neu-sm transition-all',
        'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-sime-accent',
      )}
      style={{ borderLeftColor: colors.bg }}
    >
      {showDate ? (
        <div className="shrink-0 self-center pr-2 border-r border-sime-border-subtle/60">
          <p className="text-base font-bold text-sime-text-primary leading-none">{dateDay}</p>
          <p className="text-[10px] text-sime-text-tertiary mt-1 uppercase tracking-wider">{dateMonth}</p>
        </div>
      ) : null}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-bold text-sime-accent truncate">{ticket.correlativo}</span>
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-semibold leading-none"
            style={{ backgroundColor: colors.bg, color: colors.text }}
          >
            {STATE_LABELS[ticket.estadoOt]}
          </span>
          {ticket.prioridad ? (
            <span
              className="inline-flex items-center gap-0.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700"
              aria-label="Alta prioridad"
            >
              <AlertCircle className="h-3 w-3" />
              Alta prioridad
            </span>
          ) : null}
        </div>

        <p className="mt-1 text-xs text-sime-text-secondary truncate">
          {/* NPL tickets carry their activity name in `servicio` (no planMatriz). */}
          {ticket.planMatriz?.nombre ?? ticket.servicio ?? ticket.tipoOt?.nombre ?? '—'}
        </p>

        {ticket.ubicacion?.nombre ? (
          <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-sime-text-tertiary">
            <MapPin className="h-3 w-3" />
            {ticket.ubicacion.nombre}
          </p>
        ) : null}
      </div>

      <div className="flex items-center gap-1.5 shrink-0 self-center">
        {/* Labeled here: the row is three lines tall and the tickets that
            carry documents are the finished ones, which show no action
            buttons — so the words fit exactly where they're needed. */}
        <DocumentosBadge
          ticketId={ticket.id}
          correlativo={ticket.correlativo}
          documentos={ticket.documentos}
          size="sm"
          labeled
        />
        {isExecutable ? (
          <>
            <button
              type="button"
              onClick={handleReschedule}
              className="p-2 rounded-xl text-sime-accent hover:bg-sime-accent/10 transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-sime-accent min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label={`Reprogramar ${ticket.correlativo}`}
              title="Reprogramar a otra fecha"
            >
              <CalendarClock className="h-4 w-4" />
            </button>
            <NeuButton
              variant="primary"
              size="sm"
              onClick={handleExecute}
            >
              <Wrench className="h-3 w-3 mr-1" />
              Ejecutar
            </NeuButton>
          </>
        ) : null}
      </div>

      {rescheduleOpen ? (
        <RescheduleDialog
          open
          onClose={() => { setRescheduleOpen(false); }}
          ticketId={ticket.id}
          correlativo={ticket.correlativo}
          currentDate={ticket.fecha}
        />
      ) : null}
    </div>
  );
}
